/**
 * Utility to verify camera & microphone hardware availability and browser permissions.
 */

export const checkCallMediaPermissions = async ({ isVideo = false } = {}) => {
  if (!navigator.mediaDevices || !navigator.mediaDevices.enumerateDevices) {
    return {
      ok: false,
      hasMic: false,
      hasCamera: false,
      permissionGranted: false,
      errorType: 'UNSUPPORTED_BROWSER',
      message: 'Your browser does not support audio/video call permissions.'
    };
  }

  try {
    const devices = await navigator.mediaDevices.enumerateDevices();
    const hasMic = devices.some(d => d.kind === 'audioinput');
    const hasCamera = devices.some(d => d.kind === 'videoinput');

    if (!hasMic) {
      return {
        ok: false,
        hasMic: false,
        hasCamera,
        permissionGranted: false,
        errorType: 'NO_MIC',
        message: 'No microphone detected. Please connect a microphone.'
      };
    }

    if (isVideo && !hasCamera) {
      return {
        ok: false,
        hasMic,
        hasCamera: false,
        permissionGranted: false,
        errorType: 'NO_CAM',
        message: 'No camera detected. Please connect a webcam for video calls.'
      };
    }

    // Request stream permission
    const constraints = { audio: true, video: isVideo };
    const stream = await navigator.mediaDevices.getUserMedia(constraints);
    
    // Release tracks after verification
    stream.getTracks().forEach(track => track.stop());

    return {
      ok: true,
      hasMic,
      hasCamera,
      permissionGranted: true,
      errorType: null,
      message: 'Permissions granted.'
    };
  } catch (err) {
    console.warn('Call Media Permission Error:', err);
    let errorType = 'PERMISSION_DENIED';
    let message = 'Microphone or camera permission was denied. Please allow media access in your browser.';

    if (err.name === 'NotFoundError' || err.name === 'DevicesNotFoundError') {
      errorType = isVideo ? 'NO_CAM' : 'NO_MIC';
      message = 'Required media device not found.';
    } else if (err.name === 'NotAllowedError' || err.name === 'PermissionDeniedError') {
      errorType = 'PERMISSION_DENIED';
      message = 'Microphone/camera access blocked. Please click the camera icon in your address bar to allow permissions.';
    }

    return {
      ok: false,
      hasMic: false,
      hasCamera: false,
      permissionGranted: false,
      errorType,
      message
    };
  }
};
